import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { MapPin, Clock, Ticket, ArrowUpRight, Loader2 } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Container, FadeIn, SectionLabel } from "@/components/Section";
import SEOHead from "@/components/SEOHead";
import StayConnected from "@/components/StayConnected";
import { IMG } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { key: "all", label: "All" },
  { key: "festival", label: "Festival" },
  { key: "retreat", label: "Retreat" },
  { key: "workshop", label: "Workshop" },
  { key: "family", label: "Family" },
  { key: "xr", label: "XR" },
  { key: "nightlife", label: "Nightlife" },
];

function formatDate(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}/.test(iso)) return iso;
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function WhatsOn() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  // Honour a `?filter=...` URL param so deep-links from cards on other pages
  // can pre-filter the calendar (e.g. /whats-on?filter=nightlife).
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get("filter") || "all";
  const setFilter = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "all") {
      params.delete("filter");
    } else {
      params.set("filter", next);
    }
    setSearchParams(params, { replace: true });
  };
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/events`)
      .then((r) => {
        if (!cancelled) setEvents(r.data || []);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.message || "Failed to load events");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = useMemo(
    () =>
      filter === "all"
        ? events
        : events.filter(
            (e) =>
              e.display_category === filter || e.category === filter
          ),
    [events, filter],
  );

  return (
    <div data-testid="whats-on-page">
      <SEOHead
        cmsPage="whats-on"
        title="What's On · Immersive Shows, Experiences, Workshops & Festivals · Temple of Kulture"
        description="Every upcoming TOK experience, workshop, festival and immersive show — filter by world. Afro Anime Socials, Immersive Brunch, Windrush, LEVEL UP showcases & more. Medway · London · Kent."
        image={IMG.vr}
      />
      <PageHero
        cmsPage="whats-on"
        eyebrow="What's On"
        title={
          <>
            The full <br />
            <span className="text-amber-400">calendar.</span>
          </>
        }
        subtitle="Every upcoming experience, workshop, festival and community programme — filter by world."
        image={IMG.vr}
        testId="whats-on-hero"
      />

      <section className="py-24 sm:py-32">
        <Container>
          <div className="flex flex-wrap items-center gap-2 mb-12">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                data-testid={`filter-${f.key}-button`}
                onClick={() => setFilter(f.key)}
                className={`px-5 py-3 text-[11px] uppercase tracking-[0.25em] font-bold transition-colors border ${
                  filter === f.key
                    ? "bg-amber-400 text-black border-amber-400"
                    : "border-white/10 text-zinc-300 hover:border-amber-400 hover:text-amber-400"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {loading && (
            <div
              data-testid="events-loading"
              className="py-24 flex items-center justify-center text-zinc-500 gap-3"
            >
              <Loader2 className="h-5 w-5 animate-spin text-amber-400" />
              Loading the calendar…
            </div>
          )}

          {error && (
            <p data-testid="events-error" className="text-red-400">
              {error}
            </p>
          )}

          {!loading && !error && filtered.length === 0 && (
            <p data-testid="events-empty" className="text-zinc-400 py-16">
              No events in this world yet — check back soon or join the mailing
              list for drops.
            </p>
          )}

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((e, i) => (
              <FadeIn key={e.id} delay={i * 0.04}>
                <Link
                  to={`/whats-on/${e.id}`}
                  data-testid={`event-card-${e.id}`}
                  className="group flex flex-col h-full border border-white/5 hover:border-amber-400/40 bg-zinc-950 transition-colors overflow-hidden"
                >
                  <div className="relative aspect-[16/10] overflow-hidden">
                    <div
                      className="absolute inset-0 bg-cover bg-center transition-transform duration-[1500ms] group-hover:scale-110"
                      style={{ backgroundImage: `url(${e.image})` }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    {e.availability === "sold_out" && (
                      <div className="absolute inset-0 bg-black/70 flex items-center justify-center">
                        <span className="bg-white text-black text-[10px] uppercase tracking-[0.3em] font-bold px-4 py-2">
                          Sold out
                        </span>
                      </div>
                    )}
                    {e.tag && e.availability !== "sold_out" && (
                      <span className="absolute top-3 left-3 bg-amber-400 text-black text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1">
                        {e.tag}
                      </span>
                    )}
                    {e.availability === "low" && (
                      <span className="absolute top-3 right-3 bg-amber-400/95 text-black text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1 tok-pulse-gold">
                        Low availability
                      </span>
                    )}
                    {e.availability !== "low" && e.availability !== "sold_out" && (
                      <span className="absolute top-3 right-3 bg-black/70 backdrop-blur-md border border-white/10 text-amber-400 text-[10px] uppercase tracking-[0.2em] font-bold px-2.5 py-1">
                        {e.category}
                      </span>
                    )}
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">
                      {formatDate(e.date)}
                    </p>
                    <h3 className="mt-3 font-display font-bold text-xl leading-tight">
                      {e.title}
                    </h3>
                    <p className="mt-3 text-sm text-zinc-300 leading-relaxed flex-1">
                      {e.description}
                    </p>
                    <div className="mt-5 grid gap-2 text-xs text-zinc-400">
                      <span className="flex items-center gap-2">
                        <Clock className="h-3.5 w-3.5 text-amber-400" />
                        {e.time}
                      </span>
                      <span className="flex items-center gap-2">
                        <MapPin className="h-3.5 w-3.5 text-amber-400" />
                        {e.location}
                      </span>
                      <span className="flex items-center gap-2">
                        <Ticket className="h-3.5 w-3.5 text-amber-400" />
                        {e.price}
                      </span>
                    </div>
                    <span
                      data-testid={`book-${e.id}`}
                      className={`mt-6 inline-flex items-center justify-between px-5 py-3.5 font-bold uppercase tracking-[0.25em] text-[10px] transition-colors ${
                        e.availability === "sold_out"
                          ? "bg-zinc-800 text-zinc-500"
                          : "bg-amber-400 group-hover:bg-amber-300 text-black"
                      }`}
                    >
                      {e.availability === "sold_out" ? "Join the waitlist" : "View & Book"}
                      <ArrowUpRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </FadeIn>
            ))}
          </div>
        </Container>
      </section>

      {/* WhatsApp opt-in — event alerts the moment they drop */}
      <StayConnected
        variant="whatsapp"
        pageSource="whats-on"
        testid="whats-on-stay"
        whatsappHeadline="GET EVENT ALERTS INSTANTLY"
        whatsappTagline="New shows, ticket releases and last-minute drops — straight to your WhatsApp before they sell out."
        whatsappCta="Join the Channel"
        bullets={[
          "Tickets the moment they're on sale",
          "New immersive shows & XR experiences",
          "Festival lineup announcements",
          "Late-release seats & waitlist openings",
          "Pop-up and exclusive previews",
        ]}
      />
    </div>
  );
}
